import { HandLandmarker, FilesetResolver, type HandLandmarkerResult, type NormalizedLandmark, type Landmark } from '@mediapipe/tasks-vision'
import type { HandFrame, Handedness } from '@ar-suite/core'

export interface HandTrackerOptions {
  wasmPath: string
  modelPath: string
  delegate: 'GPU' | 'CPU'
  numHands: number
  minHandDetectionConfidence: number
  minHandPresenceConfidence: number
  minTrackingConfidence: number
}

export const HAND_TRACKER_DEFAULTS: HandTrackerOptions = {
  wasmPath: '/wasm',
  modelPath: '/models/hand_landmarker.task',
  delegate: 'GPU',
  numHands: 2,
  minHandDetectionConfidence: 0.5,
  minHandPresenceConfidence: 0.6,
  minTrackingConfidence: 0.6,
}

export interface HandTrackerResult {
  hands: HandFrame[]
  inferenceMs: number
}

/**
 * MediaPipe Hand Landmarker wrapper → portable HandFrames with metric worldLandmarks.
 * GOTCHA: MediaPipe reports handedness assuming a MIRRORED (selfie) image; our frames are
 * unmirrored, so the label is flipped here — `handedness` is the subject's true hand.
 */
export class HandTracker {
  readonly options: HandTrackerOptions
  private landmarker: HandLandmarker | null = null
  private lastTimestamp = -1
  private frames: HandFrame[] = []

  constructor(options: Partial<HandTrackerOptions> = {}) {
    this.options = { ...HAND_TRACKER_DEFAULTS, ...options }
  }

  get ready(): boolean {
    return this.landmarker !== null
  }

  async init(): Promise<void> {
    if (this.landmarker) return
    const vision = await FilesetResolver.forVisionTasks(this.options.wasmPath)
    const create = (delegate: 'GPU' | 'CPU') =>
      HandLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: this.options.modelPath, delegate },
        runningMode: 'VIDEO',
        numHands: this.options.numHands,
        minHandDetectionConfidence: this.options.minHandDetectionConfidence,
        minHandPresenceConfidence: this.options.minHandPresenceConfidence,
        minTrackingConfidence: this.options.minTrackingConfidence,
      })
    try {
      this.landmarker = await create(this.options.delegate)
    } catch (e) {
      if (this.options.delegate === 'GPU') {
        console.warn('[ar-suite] hand GPU delegate failed, falling back to CPU', e)
        this.landmarker = await create('CPU')
      } else throw e
    }
  }

  detect(video: HTMLVideoElement | HTMLCanvasElement | ImageBitmap | OffscreenCanvas, timestampMs: number): HandTrackerResult {
    if (!this.landmarker) throw new Error('HandTracker.init() has not completed')
    if (timestampMs <= this.lastTimestamp) timestampMs = this.lastTimestamp + 1
    this.lastTimestamp = timestampMs
    const t0 = performance.now()
    const result: HandLandmarkerResult = this.landmarker.detectForVideo(video, timestampMs)
    const inferenceMs = performance.now() - t0
    const width = 'videoWidth' in video ? video.videoWidth : video.width
    const height = 'videoHeight' in video ? video.videoHeight : video.height
    const n = result.landmarks.length
    for (let h = 0; h < n; h++) {
      const lms = result.landmarks[h]!
      const world = result.worldLandmarks[h]
      let frame = this.frames[h]
      if (!frame || frame.landmarks.length !== lms.length * 3) {
        frame = {
          landmarks: new Float32Array(lms.length * 3),
          worldLandmarks: new Float32Array(lms.length * 3),
          handedness: 'right',
          score: 0,
          width,
          height,
          timestamp: timestampMs,
        }
        this.frames[h] = frame
      }
      pack(frame.landmarks, lms)
      if (world) pack(frame.worldLandmarks! as Float32Array, world)
      const cat = result.handedness[h]?.[0]
      // flip: MediaPipe assumes mirrored input, our frames are unmirrored
      frame.handedness = ((cat?.categoryName?.toLowerCase() === 'left' ? 'right' : 'left') as Handedness)
      frame.score = cat?.score ?? 0
      frame.width = width
      frame.height = height
      frame.timestamp = timestampMs
    }
    return { hands: this.frames.slice(0, n), inferenceMs }
  }

  close(): void {
    this.landmarker?.close()
    this.landmarker = null
  }
}

function pack(out: Float32Array, lms: NormalizedLandmark[] | Landmark[]): void {
  for (let i = 0; i < lms.length; i++) {
    const l = lms[i]!
    out[i * 3] = l.x
    out[i * 3 + 1] = l.y
    out[i * 3 + 2] = l.z
  }
}
